import { useTranslation } from 'react-i18next';
import { statusLabels } from '@/features/bookings';
import type { BookingResponse, ConversationDetail } from '@/shared/api';
import { useApiProblem } from '@/shared/api/useApiProblem';
import { formatOccupancy } from '@/shared/lib/occupancy';
import { useAutomationControl } from '../automation/useAutomationControl';
import { channelLabel } from '../inboxModel';

function shortDate(iso: string): string {
  return new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
  });
}

/** „3 Nächte“ – ohne lesbare Daten keine Zahl statt einer erfundenen 0. */
function stay(booking: BookingResponse): string {
  const ms =
    Date.parse(booking.checkOutDate.slice(0, 10)) - Date.parse(booking.checkInDate.slice(0, 10));
  const range = `${shortDate(booking.checkInDate)}–${shortDate(booking.checkOutDate)}`;
  if (Number.isNaN(ms)) return range;
  const nights = Math.max(0, Math.round(ms / 86_400_000));
  return `${range} · ${nights} ${nights === 1 ? 'Nacht' : 'Nächte'}`;
}

const CHIP =
  'rounded-pill bg-card-alt text-ink inline-flex min-h-[30px] items-center px-2.5 text-xs font-bold';

/** Das Wichtigste zum Aufenthalt in einer Zeile über dem Verlauf; Einzelheiten stehen im Kontext. */
export function ConversationChips({
  thread,
  booking,
  propertyName,
  live,
}: {
  thread: ConversationDetail;
  booking: BookingResponse | null;
  propertyName: string | null;
  /** Ohne Server (Vorschau) gibt es keine Automatik-Abfrage. */
  live: boolean;
}) {
  const chips = [
    booking
      ? `${channelLabel(thread.channel)} · ${booking.bookingReference}`
      : channelLabel(thread.channel),
    booking ? statusLabels[booking.status] : null,
    propertyName,
    booking ? stay(booking) : null,
    booking ? formatOccupancy(booking) : null,
  ].filter((chip): chip is string => Boolean(chip));
  return (
    <ul aria-label="Kontext" className="hidden flex-wrap gap-1.5 sm:flex">
      {chips.map((chip) => (
        <li key={chip} className={CHIP}>
          {chip}
        </li>
      ))}
      {live && <AutomationChip conversationId={thread.id} />}
    </ul>
  );
}

/** KI-Modus; ein Ladefehler sieht nicht aus wie „aus“, sonst bliebe verborgen, dass sie antworten könnte. */
function AutomationChip({ conversationId }: { conversationId: string }) {
  const { t } = useTranslation('automation');
  const { query } = useAutomationControl(conversationId);
  const problem = useApiProblem(query.error);
  const view = query.data;
  if (!view) {
    return query.isError && problem && problem.status !== 403 ? (
      <li className={CHIP}>KI-Status unbekannt</li>
    ) : null;
  }
  if (view.control.mode === 'Disabled' && !view.control.humanPaused) return null;
  return <li className={CHIP}>{view.control.humanPaused ? t('paused') : t(view.control.mode)}</li>;
}
